import { useState } from 'react'
import { Fab, IconButton, Link } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PageLoading from '../../../components/PageLoading/PageLoading'
import TableHeader from '../../../components/TableHeader/TableHeader'
import InformationTable from '../../../components/Table/InformationTable'
import { textFilter } from '../../../utils/table'
import {
  createVideo,
  deleteVideo,
  getVideoList,
  updateVideo
} from '../../../utils/api/materialDocente'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import { formatSecondsToMinutes } from '../../../utils/time'
import ModalConfirmacion from '../../../modals/ModalConfirmacion/ModalConfirmacion'
import { isRolBrain } from '../../../utils/auth'
import ModalEditarVideo from '../../../modals/ModalEditarVideo/ModalEditarVideo'
import ModalNuevoVideo from '../../../modals/ModalNuevoVideo/ModalNuevoVideo'
import ModalAsignaturas from '../../../modals/ModalAsignaturas/ModalAsignaturas'
import AsignaturaSelector from '../../../components/form/AsignaturaSelector/AsignaturaSelector'
import { pluralize } from '../../../utils/string'
import css from './Videos.module.css'

const Videos = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const [search, setSearch] = useState('')
  const [videoSeleccionado, setVideoSeleccionado] = useState(null)
  const [isOpenEditarVideo, setIsOpenEditarVideo] = useState(false)
  const [isOpenEliminarVideo, setIsOpenEliminarVideo] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpenAsignaturasModal, setIsOpenAsignaturasModal] = useState(false)
  const [selectedAsignatura, setSelectedAsignatura] = useState(null)

  const accesoBrain = isRolBrain()

  const { isLoading, data: videoList = [] } = useQuery({
    queryKey: ['profes', 'material', 'video'],
    queryFn: () =>
      getVideoList().catch(err => {
        notification.error({
          title: 'Error al recuperar los vídeos',
          content: err
        })
        return []
      })
  })

  const { isPending: isVideoCreating, mutate: crearVideo } = useMutation({
    mutationFn: createVideo,
    onSuccess: () => {
      notification.success({
        title: 'Vídeo enviado',
        content: 'Se ha enviado el video'
      })
      queryClient.invalidateQueries({
        queryKey: ['profes', 'material', 'video']
      })
      setIsModalOpen(false)
    },
    onError: err => {
      notification.error({ title: 'Error al enviar el vídeo', content: err })
    }
  })

  const { isPending: isVideoUpdating, mutate: actualizarVideo } = useMutation({
    mutationFn: updateVideo,
    onSuccess: () => {
      queryClient.invalidateQueries(['profes', 'material', 'video'])
      notification.success({
        title: 'Vídeo actualizado',
        content: 'Datos modificados correctamente'
      })
      setIsOpenEditarVideo(false)
    },
    onError: err => {
      notification.error({ title: 'Error actualizando el vídeo', content: err })
    }
  })

  const { isPending: isVideoDeleting, mutate: borrarVideo } = useMutation({
    mutationFn: deleteVideo,
    onSuccess: () => {
      queryClient.invalidateQueries(['profes', 'material', 'video'])
      notification.success({
        title: 'Vídeo borrado',
        content: 'Se ha borrado el vídeo correctamente'
      })
      setVideoSeleccionado(null)
      setIsOpenEliminarVideo(false)
    },
    onError: err => {
      notification.error({ title: 'Error al borrar el vídeo', content: err })
    }
  })

  const handleNuevoVideo = ({ nombre, url, duracion, asignaturas }) => {
    if (isVideoCreating) return
    crearVideo({ nombre, url, duracion, asignaturas })
  }

  const handleUpdateVideo = updatedVideo => {
    if (isVideoUpdating) return
    actualizarVideo(updatedVideo)
  }

  const handleDeleteVideo = () => {
    if (isVideoDeleting) return
    borrarVideo(videoSeleccionado.id)
  }

  const filterSearch = video => {
    const matchesSearch = textFilter({
      object: video,
      fields: ['nombre', 'asignaturas'],
      search: search
    })

    const matchesAsignatura =
      !selectedAsignatura ||
      (video.asignaturas &&
        video.asignaturas.some(
          asignatura => asignatura.id === selectedAsignatura.id
        ))

    return matchesSearch && matchesAsignatura
  }

  const openModal = video => {
    setVideoSeleccionado(video)
    setIsOpenEditarVideo(true)
  }

  const closeModal = () => {
    setIsOpenEditarVideo(false)
    setVideoSeleccionado(null)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        title='Vídeos'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        actions={
          <AsignaturaSelector
            label='Asignatura'
            value={selectedAsignatura?.id || null}
            onChange={(_event, newValue) => setSelectedAsignatura(newValue)}
            className={css.asignaturaFilter}
          />
        }
      />
      <InformationTable
        details={[
          { title: 'Nombre', key: 'nombre', sortKey: 'nombreVideo' },
          { title: 'Duración', key: 'duracion' },
          {
            title: 'Asignaturas',
            key: 'asignaturas',
            sortKey: 'totalAsignaturas'
          },
          { title: 'Opciones', key: 'opciones', align: 'center' }
        ]}
        data={videoList.filter(filterSearch).map(video => ({
          nombre: (
            <Link className={css.link} href={video.url} target='_blank'>
              {video.nombre || '--'}
            </Link>
          ),
          nombreVideo: video.nombre,
          duracion: formatSecondsToMinutes(video.duracion) || '--',
          totalAsignaturas: video.asignaturas ? video.asignaturas.length : 0,
          asignaturas:
            video.asignaturas && video.asignaturas.length > 0 ? (
              <span
                className={css.asignaturasCount}
                onClick={() => {
                  setVideoSeleccionado(video)
                  setIsOpenAsignaturasModal(true)
                }}
              >
                {video.asignaturas.length}{' '}
                {pluralize('asignatura', video.asignaturas.length)}
              </span>
            ) : (
              <span className={css.sinAsignaturas}>Sin asignaturas</span>
            ),
          opciones: accesoBrain ? (
            <>
              <IconButton
                size='small'
                color='secondary'
                onClick={() => openModal(video)}
              >
                <i className='material-icons'>create</i>
              </IconButton>
              <IconButton
                size='small'
                color='error'
                onClick={() => {
                  setVideoSeleccionado(video)
                  setIsOpenEliminarVideo(true)
                }}
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          ) : null
        }))}
        isFetching={isLoading}
      />
      <ModalNuevoVideo
        open={isModalOpen}
        onSubmit={handleNuevoVideo}
        onClose={() => setIsModalOpen(false)}
        isLoading={isVideoCreating}
      />
      <ModalEditarVideo
        open={isOpenEditarVideo}
        video={videoSeleccionado}
        onClose={closeModal}
        onSubmit={handleUpdateVideo}
      />
      <ModalConfirmacion
        title='¿Seguro que quieres borrar el vídeo?'
        content='Al borrar el vídeo, dejará de estar disponible en el área de profesores, así como en el área de selección de vídeos que hay en Classfy.'
        open={isOpenEliminarVideo}
        onClose={() => setIsOpenEliminarVideo(false)}
        onClick={handleDeleteVideo}
      />
      <ModalAsignaturas
        open={isOpenAsignaturasModal}
        onClose={() => setIsOpenAsignaturasModal(false)}
        name={videoSeleccionado?.nombre || ''}
        asignaturas={videoSeleccionado?.asignaturas || []}
      />
      {accesoBrain && (
        <Fab
          className={css.nuevo}
          disabled={isVideoCreating}
          onClick={() => setIsModalOpen(true)}
          size='medium'
          color='primary'
          aria-label='add'
        >
          <i className='material-icons'>add</i>
        </Fab>
      )}
    </PageLoading>
  )
}

export default Videos
